import {
  Enum_Questiontree_Treetype,
  notNull,
  useMyVisitLazyQuery,
  useQuestionTreesLazyQuery,
} from '@cyren/common-lib';
import produce from 'immer';
import { isEmpty, keys } from 'lodash';
import { useEffect, useRef } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';

import { useLocation, useNavigate } from 'react-router';
import { convSurvey, convTree, convVisit } from '../gql-tools/utils/convert';
import { PaReportState } from '../patient/hooks/use-report';
import { PaSurveyState } from '../patient/hooks/use-survey-state';
import { Keys, PaDataRepoStateT, QuestionTreeType } from '../patient/patient-types';
import { usePaSurveyParams } from './use-pa-survey-params';
import { AthenaServices } from '../lib/athena/athena-services';
import { getPatientBasePath } from '../app/route-utils';
import { isSameDataLoadContext, shouldRunDataLoad } from './use-data-utils';

const pageSize = 200000;

export const PaDataRepoState = atom<PaDataRepoStateT>({
  key: 'DataRepoState',
  default: {
    trees: [],
    questions: [],
    translations: [],
    doctorsTerms: [],
    userNoteTrans: [],
    survey: null,
    org: null,
  },
});

// 전역 캐시 (aid -> timestamp)
const checkedAidMap = new Map<string, number>();
// 30초 동안 중복 체크 방지
const CACHE_DURATION = 30 * 1000;

export function useData({
  initData,
  disableRedirectOnInvalidReport = false,
}: { initData?: boolean; disableRedirectOnInvalidReport?: boolean } = {}) {
  const params = usePaSurveyParams();
  const nav = useNavigate();
  const location = useLocation();
  const checkedAidRef = useRef<string | null>(null);
  
  // 데이터 로드 상태 추적용 ref
  const loadedShortIdRef = useRef<string | null>(null);
  const loadedAidRef = useRef<string | null>(null);
  const loadedUuidRef = useRef<string | null>(null);
  const isRunInFlightRef = useRef(false);

  const surveyCheck = async (aid: string) => {
    const result = await AthenaServices.checkSurveyStatusByAid(aid);
    return result;
  }

  const { orgId, shortId, adhocId, referralMessageId } = params;

  const baseUrl = `${getPatientBasePath(location.pathname)}/${orgId}/${shortId}`;

  const [dataState, setDataState] = useRecoilState(PaDataRepoState);
  const setSurveyState = useSetRecoilState(PaSurveyState);
  const setReportState = useSetRecoilState(PaReportState);

  const { visit: visitFrag, trees, translations, survey, questions, doctorsTerms } = dataState;

  // TODO: optimize load survey in 1 api call
  const [fetchMyVisit, { data: dataSurveys, loading: loadingSurveys }] = useMyVisitLazyQuery({
    onCompleted: () => {
      // fetch my visit comp
    },
  });

  // REDIRECT USER TO FINAL WHEN STATUS = REPORTED | ARCHIVED
  const status = visitFrag?.status;
  const archived = visitFrag?.archived;
  const searchParams = new URLSearchParams(window.location.search);
  const currentAid = searchParams.get('aid');
  const currentUuid = searchParams.get('uuid'); // createPreVisit으로 생성된 visit의 uuid
  const isCurrentContextLoaded = isSameDataLoadContext({
    loadedShortId: loadedShortIdRef.current,
    loadedAid: loadedAidRef.current,
    loadedUuid: loadedUuidRef.current,
    currentShortId: shortId,
    currentAid,
    currentUuid,
  });

  useEffect(() => {
    if (isCurrentContextLoaded) {
      return;
    }

    // Route context changed (e.g. cloned survey entry): clear stale visit before new fetch resolves.
    setDataState((st) => ({
      ...st,
      visit: null,
    }));
  }, [isCurrentContextLoaded, setDataState]);

  useEffect(() => {
    if (disableRedirectOnInvalidReport) {
      return;
    }

    if (!isEmpty(adhocId)) {
      // if this is an adhoc form, don't navigate them to the main finished screen, since
      // adhoc forms can be request event after the main survey has been completed
      return;
    }
    if (!shortId) {
      // The shortID is invalid, which might mean we are in a hidden element being used for printing.  Don't try to redirect
      return;
    }

    if (!isCurrentContextLoaded) {
      return;
    }
    
    // aid가 있는 경우 (Athena integration)에는 자동 리다이렉트 하지 않음
    // aid가 변경되면 새로운 설문지를 시작할 수 있도록 함
    if (currentAid) {
      // 전역 캐시 체크 (Throttling)
      const now = Date.now();
      const lastCheck = checkedAidMap.get(currentAid);
      
      if (lastCheck && (now - lastCheck < CACHE_DURATION)) {
        return;
      }

      // 로컬 ref 체크 (컴포넌트 내 중복 호출 방지)
      if (checkedAidRef.current === currentAid) {
        return;
      }

      checkedAidRef.current = currentAid;
      // 호출 시작 시점에 타임스탬프 기록 (동시 호출 방지)
      checkedAidMap.set(currentAid, now);
      
      surveyCheck(currentAid).then((result) => {
        // 결과가 오면 타임스탬프 갱신 (성공 시점 기준)
        checkedAidMap.set(currentAid, Date.now());
        
        if (result.completed === true) {
          nav(`${baseUrl}/terminated`);
        }
      }).catch(err => {
        // 에러 발생 시 캐시에서 제거하여 재시도 가능하게 함 (선택사항, 여기서는 유지)
        console.warn('설문 상태 체크 실패:', err);
      });
    }

    // 일반 설문지의 경우에만 완료 상태 체크 (done, reported 모두 체크)
    if (status === 'done' || status === 'reported' || archived) {
      nav(`${baseUrl}/terminated`);
    }
  }, [disableRedirectOnInvalidReport, status, archived, currentAid, isCurrentContextLoaded, shortId]);

  const [fetchTrees, { data: dataTrees, loading: loadingTrees }] = useQuestionTreesLazyQuery();

  useEffect(() => {
    async function run() {
      if (shortId == null || isRunInFlightRef.current) return;

      isRunInFlightRef.current = true;

      const errors: string[] = [];

      try {
        const { data: visitData, error } = await fetchMyVisit({
          fetchPolicy: 'network-only',
          variables: {
            input: {
              shortId,
              referralMessageId,
              ...(currentAid && { appointmentId: currentAid }), // appointmentId가 있을 때만 추가
              ...(currentUuid && { uuid: currentUuid }), // createPreVisit으로 생성된 visit의 uuid
              createIfMissing: true,
            },
          },
        });

        if (error) {
          // TODO log and report an error
          errors.push('There was an error loading data');

          setDataState((st) => {
            return {
              ...st,
              errors,
              init: true,
            };
          });
          return;
        }

        // DECRYPT DATA
        // 방어 코드: survey.data가 배열일 경우 처리 및 로그 강화
        let rawSurveyData = visitData?.myVisit?.survey?.data;
        if (Array.isArray(rawSurveyData)) {
          console.warn('Survey data is an array, taking first element');
          [rawSurveyData] = rawSurveyData;
        } else if (!rawSurveyData && visitData?.myVisit?.survey && !visitData?.myVisit?.survey?.data) {
          // 구조가 survey: { id: ..., attributes: ... } 처럼 data 래퍼가 없는 경우 (일부 GQL 설정)
          // 혹은 visitData.myVisit.survey 자체가 데이터 객체인 경우
          // rawSurveyData = visitData?.myVisit?.survey;
        }
        
        const surveyData = convSurvey(rawSurveyData);
        const visit = convVisit(visitData?.myVisit?.patientVisit?.data);

        if (!surveyData) {
          console.error('Survey data not found for shortId:', shortId, 'visitData:', visitData);
          setDataState((st) => ({
            ...st,
            init: true,
            errors: ['Survey not found or access denied'],
          }));
          return;
        }

        const qsTreeResp = fetchTrees({
          fetchPolicy: 'network-only',
          variables: {
            filters: {
              survey: {
                id: {
                  eq: visit?.survey_id?.toString()
                },
              },
              enabled: {
                eq: true,
              },
              treeKey: {
                in: surveyData?.questionTreeKeys || [],
              },
              treeType: {
                eq: Enum_Questiontree_Treetype.Step,
              },
            },
            pagination: {
              pageSize,
            },
          },
        });

        const result = await Promise.all([qsTreeResp]);
        const treesData = result[0];

        const nTrees = treesData.data?.questionTrees?.data.map(convTree).filter(notNull) || [];
        const org = surveyData?.organization;

        if (!surveyData) return;

        const { questionTreeKeys } = surveyData || {};
        const nTreeKeys = questionTreeKeys?.filter((f) => {
          const tree = nTrees.find((item) => item.treeKey === f && item.enabled);
          return !!tree;
        }) || [];

        const nSurvey = {
          ...surveyData,
          questionTreeKeys: nTreeKeys,
        };

        // Mark this request context as resolved only after new visit/survey data is ready.
        loadedShortIdRef.current = shortId;
        loadedAidRef.current = currentAid;
        loadedUuidRef.current = currentUuid;

        setSurveyState(
          produce(
            visit?.surveyState?.surveyState || {
              adaptedTreeKeys: [],
            },
            (draft) => {
              keys(visit?.surveyState?.surveyState).forEach((key) => {
                // @ts-ignore
                draft[key] = visit?.surveyState?.surveyState?.[key];
              });
            },
          ),
        );

        setReportState(
          produce(
            visit?.surveyState?.reportState || {
              globalAnswerMap: {},
              treeStates: [],
              nextTreeKeys: [],
              presentIllness: [],
            },
            (draft) => {
              keys(visit?.surveyState?.reportState).forEach((key) => {
                // @ts-ignore
                draft[key] = visit?.surveyState?.reportState?.[key];
              });
            },
          ),
        );

        setDataState((st) => {
          return {
            ...st,
            visit,
            init: true,
            survey: nSurvey,
            org,
            trees: nTrees,
            errors,
          };
        });
      } finally {
        isRunInFlightRef.current = false;
      }
    }

    if (
      shouldRunDataLoad({
        initData,
        shortId,
        hasSurveyData: Boolean(dataState.survey),
        loadedShortId: loadedShortIdRef.current,
        loadedAid: loadedAidRef.current,
        loadedUuid: loadedUuidRef.current,
        currentAid,
        currentUuid,
        isRunInFlight: isRunInFlightRef.current,
      })
    ) {
      run();
    }
  }, [shortId, initData, currentAid, currentUuid]);

  const isLoading = loadingSurveys || loadingTrees;

  // loadingTrans
  // loadingTrees
  // loadingSurvey
  // isLoading

  function getSurvey() {
    return survey;
  }

  function getTrees(searchKey?: string) {
    if (!searchKey) return trees || [];
    return trees?.filter((tree) => tree.treeKey.includes(searchKey)) || [];
  }

  function getTreeByKey(treeKey: string) {
    return trees?.find((tree) => tree.treeKey === treeKey) || null;
  }

  function getTreesByType(treeType: Enum_Questiontree_Treetype | null) {
    return trees?.filter((tree) => tree.treeType === treeType) || [];
  }

  function getQuestionByKey(key?: string | null) {
    if (key == null) return null;
    return questions?.find((q) => q.questionKey === key);
  }

  function getQsById(id?: string | null) {
    if (id == null) return null;
    return questions?.find((q) => q.id === id);
  }

  function getReportLabel(key?: string) {
    if (!key) return undefined;
    const question = getQuestionByKey(key);
    return question?.reportLabel;
  }

  function getQuestionsByKeys(qsKeys: string[]) {
    return qsKeys?.map((key) => getQuestionByKey(key))?.filter(notNull);
  }

  function getTransByKey(key: string) {
    return translations?.find((q) => q.key === key);
  }

  function getTranssByKeys(transKeys: string[]) {
    return transKeys.map((key) => getTransByKey(key)).filter(notNull);
  }

  function getTermByKey(key: string) {
    return doctorsTerms?.find((q) => q.key === key);
  }

  function getTermsByKeys(tKeys: Keys) {
    return tKeys.map((key) => getTermByKey(key)).filter(notNull);
  }

  function updateTree(nTree?: QuestionTreeType | null) {
    if (!nTree) {
      return;
    }

    setDataState((st) => {
      return produce(st, (draft) => {
        draft.trees =
          trees?.map((tree) => {
            if (tree.treeKey === nTree.treeKey) {
              return nTree;
            }
            return tree;
          }) || null;
      });
    });
  }

  // dataState?.questions
  // dataState?.translations

  return [
    {
      dataState,
      isLoading,
      loadingSurvey: loadingSurveys,
      loadingTrees,

      dataSurveys,
      dataTrees,
      surveyId: survey?.id,
    },
    {
      updateTree,
      setDataState,
      getSurvey,
      getTreeByKey,
      getTrees,
      getTreesByType,
      getQuestionByKey,
      getTermsByKeys,
      getTermByKey,
      getQsById,
      getReportLabel,
      getTransByKey,
      getTranssByKeys,
      getQuestionsByKeys,
    },
  ] as const;
}
